import React from 'react';
import { Tooltip } from '@mantine/core';
import { IconShieldCheck, IconAlertCircle, IconAlertTriangle } from '@tabler/icons-react';
import { RiskLevel } from '../../schemas/common';
import { useI18n } from '../../locales/i18n-context';
import { TranslationKey } from '../../locales/types';
import './risk-indicator.css';

interface RiskIndicatorProps {
  risk: RiskLevel;
  inProgressCount?: number;
  label?: string;
  iconOnly?: boolean;
}

const CONFIG: Record<
  RiskLevel,
  { color: string; bg: string; Icon: React.ElementType; pulse?: boolean }
> = {
  LOW: { color: '#10B981', bg: '#10B98120', Icon: IconShieldCheck },
  MEDIUM: { color: '#F59E0B', bg: '#F59E0B20', Icon: IconAlertCircle },
  HIGH: { color: '#EF4444', bg: '#EF444420', Icon: IconAlertTriangle, pulse: true },
  UNKNOWN: { color: '#6B7280', bg: '#6B728020', Icon: IconShieldCheck },
};

export const RiskIndicator: React.FC<RiskIndicatorProps> = ({
  risk,
  inProgressCount,
  label,
  iconOnly,
}) => {
  const { t } = useI18n();
  const cfg = CONFIG[risk];

  const tooltipLabel =
    risk === 'MEDIUM' || risk === 'HIGH'
      ? `${inProgressCount ?? 0} in-progress transactions`
      : undefined;

  // Automatically determine the localized label if not provided
  const riskLabel = label ?? t(`risk.${risk.toLowerCase()}` as TranslationKey);

  const badge = (
    <span
      className={cfg.pulse ? 'risk-indicator--pulse' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 10px',
        borderRadius: 4,
        background: cfg.bg,
        color: cfg.color,
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        cursor: tooltipLabel ? 'default' : undefined,
      }}
    >
      <cfg.Icon size={12} />
      {!iconOnly && riskLabel}
    </span>
  );

  if (tooltipLabel) {
    return <Tooltip label={tooltipLabel}>{badge}</Tooltip>;
  }
  return badge;
};
